import React from 'react';
import {
  TextField,
  Stack,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Divider,
  Box,
  Chip,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery } from '@tanstack/react-query';
import { organizationsApi, hexahqCacheApi, devicesApi } from '../../api/services';
import type { HexahqAccount } from '../../api/services';
import type { Location, CreateLocationData, Device } from '../../api/config';
import { useHexahqAutocomplete } from '../../hooks/useHexahqAutocomplete';
import { FormDialog } from '../FormDialog';
import { StatusMonitoringFields } from './StatusMonitoringFields';
import { TimezoneSelect, DEFAULT_TIMEZONE } from './TimezoneSelect';
import { DigestRecipientFields, restockCadenceErrors } from './DigestRecipientFields';
import { DigestPreferencesFields } from './DigestPreferencesFields';
import { FulfillmentFields } from './FulfillmentFields';
import { AccountManagerFields } from './AccountManagerFields';

interface LocationFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateLocationData) => void;
  initialData?: Location;
  onExited?: () => void;
  error?: string | null;
  isPending?: boolean;
}

const DEFAULT_FORM_DATA: CreateLocationData = {
  device_id: '',
  name: '',
  timezone: DEFAULT_TIMEZONE,
  active: true,
  alerts_enabled: true,
  internal: false,
  organization_id: undefined,
  power_configuration: 'Unknown',
  digest_recipient_email: '',
  digest_recipient_name: '',
  digest_preferences: {
    restock: true,
    daily_usage: false,
    oos: false,
    rma: false,
  },
  digest_restock_day_of_week: null,
  restock_interval_weeks: 1,
  restock_anchor_date: null,
  show_reply_by_time: false,
  quote_confirmation_deadline: '',
  collect_account_info: '',
  uses_stock_truck: false,
  stock_truck_delivery_day: null,
  ground_transit_business_days: null,
  freight_option: '',
  stock_truck_route: '',
  hexahq_account_id: null,
  auto_send_enabled: false,
  digest_send_time: '',
};

// Maps a send day-of-week (0=Sunday..6=Saturday) to the default
// quote-confirmation deadline (next business day at 2PM).
const DAY_OF_WEEK_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

const defaultDeadlineForSendDay = (day: number): string => {
  // Mon-Thu (1-4) → next day; Fri/Sat/Sun (5,6,0) → Monday.
  const nextDay = day >= 1 && day <= 4 ? day + 1 : 1;
  return `${DAY_OF_WEEK_NAMES[nextDay]} 2PM`;
};

export const LocationForm: React.FC<LocationFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  onExited,
  error: apiError,
  isPending,
}) => {
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.getAll,
  });

  const [formData, setFormData] = React.useState<CreateLocationData>({
    ...DEFAULT_FORM_DATA,
    ...initialData,
  });

  const account = useHexahqAutocomplete<HexahqAccount>({
    searchFn: hexahqCacheApi.accountSearch,
    queryKey: 'hexahq_account_search',
    formatLabel: (opt) => opt.name ? `${opt.hexahq_id}: ${opt.name}` : String(opt.hexahq_id),
    isOptionEqual: (a, b) => a.hexahq_id === b.hexahq_id,
    enabled: open,
    noMatchText: 'No matching accounts',
  });

  const [errors, setErrors] = React.useState<{ device_id?: string; account_manager_email?: string }>({});
  const [pendingDevices, setPendingDevices] = React.useState<Device[]>([]);
  // True when quote_confirmation_deadline was auto-filled from the send day
  // during this form session. Drives the helper-text reminder that the
  // value still needs Update to persist.
  const [deadlineWasAutoFilled, setDeadlineWasAutoFilled] = React.useState(false);

  // Fetch all devices for autocomplete in create mode
  const { data: allDevices = [], isLoading: isLoadingDevices } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.getAll(),
    enabled: open && !initialData,
  });

  const deviceLookup = React.useMemo(
    () => new Map(allDevices.map(d => [d.device_id, d])),
    [allDevices]
  );

  const deviceStatus = React.useMemo(() => {
    const id = formData.device_id?.trim().toLowerCase();
    if (!id) return null;
    const existing = deviceLookup.get(id);
    if (!existing) return { type: 'new' as const };
    if (!existing.location_id) return { type: 'unassigned' as const, device: existing };
    return { type: 'assigned' as const, device: existing, locationName: existing.location?.name || `Location #${existing.location_id}` };
  }, [formData.device_id, deviceLookup]);

  const deviceHelperText = React.useMemo(() => {
    if (errors.device_id) return errors.device_id;
    const id = formData.device_id || '';
    if (!id) return 'Optional — leave blank to create the location without a device, or assign one via Edit later';
    if (deviceStatus?.type === 'assigned')
      return `Already assigned to ${deviceStatus.locationName}`;
    if (deviceStatus?.type === 'unassigned')
      return 'Existing device — currently unassigned';
    // New device — show progressive feedback
    if (id.length < 24) return `${id.length}/24 characters`;
    if (!/^[a-f0-9]{24}$/i.test(id)) return 'Must be a 24-character hex string';
    return 'New device — will be created';
  }, [formData.device_id, deviceStatus, errors.device_id]);

  const [deviceDropdownOpen, setDeviceDropdownOpen] = React.useState(false);
  const filterDevices = React.useCallback((devices: Device[], input: string) => {
    const lower = input.toLowerCase();
    if (!lower) return devices;
    return devices.filter(d =>
      d.device_id.toLowerCase().includes(lower) ||
      (d.label || '').toLowerCase().includes(lower) ||
      (d.location?.name || '').toLowerCase().includes(lower)
    );
  }, []);
  const hasFilteredDevices = React.useMemo(
    () => filterDevices(allDevices, formData.device_id || '').length > 0,
    [filterDevices, allDevices, formData.device_id]
  );

  React.useEffect(() => {
    if (initialData) {
      // Auto-suggest a deadline when day is set but deadline is blank.
      // This catches legacy data where the day was populated before the
      // deadline field existed. sc-6506: only while the reply-by toggle is on
      // — a hidden deadline is never auto-filled.
      const day = initialData.digest_restock_day_of_week;
      const currentDeadline = (initialData.quote_confirmation_deadline ?? '').trim();
      const shouldAutoSuggest = !!initialData.show_reply_by_time
        && day !== null && day !== undefined && currentDeadline === '';

      setFormData(
        shouldAutoSuggest
          ? { ...initialData, quote_confirmation_deadline: defaultDeadlineForSendDay(day as number) }
          : initialData
      );
      setDeadlineWasAutoFilled(shouldAutoSuggest);
      setPendingDevices([]);
      if (initialData.hexahq_account_id) {
        account.setSelectedOption({ hexahq_id: initialData.hexahq_account_id, name: initialData.hexahq_account_name || '' });
        account.setInputValue(String(initialData.hexahq_account_id));
      } else {
        account.setSelectedOption(null);
        account.setInputValue('');
      }
    } else if (open) {
      setFormData({ ...DEFAULT_FORM_DATA });
      setDeadlineWasAutoFilled(false);
      setPendingDevices([]);
      account.setSelectedOption(null);
      account.setInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    let deviceId: string | undefined;
    if (!initialData) {
      deviceId = formData.device_id?.trim();

      if (deviceId) {
        // Validate: must be a 24-character hex string (Particle coreid)
        if (!/^[a-f0-9]{24}$/i.test(deviceId)) {
          setErrors({ device_id: 'Device ID must be a 24-character hex string (Particle coreid)' });
          return;
        }

        // Block submission if device is already assigned to another location
        if (deviceStatus?.type === 'assigned') {
          setErrors({ device_id: `Already assigned to ${deviceStatus.locationName}` });
          return;
        }
      }
    }

    // Validate account manager email if provided
    const amEmail = formData.digest_preferences?.account_manager_email?.trim();
    if (amEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(amEmail)) {
      setErrors({ account_manager_email: 'Please enter a valid email address' });
      return;
    }

    // Restock cadence must be valid (interval >= 1; anchor present when
    // interval > 1). The offending field already shows its inline error
    // inside DigestRecipientFields, so just block submission.
    if (Object.keys(restockCadenceErrors(formData)).length > 0) {
      return;
    }

    // Transform data before submission. `device_id` is set to the trimmed +
    // lowercased value when present; when blank, it is `undefined` so JSON
    // serialization omits the key entirely (creates a deviceless location).
    // Delivery time fields: form state preserves both sides for in-session UX
    // when toggling Stock Truck, but submission nulls the irrelevant side so
    // the DB never holds an inconsistent pair.
    const transformedData: CreateLocationData = {
      ...formData,
      device_id: deviceId ? deviceId.toLowerCase() : undefined,
      ...(pendingDevices.length > 0 ? { assign_device_ids: pendingDevices.map(d => d.id) } : {}),
      hexahq_account_id: formData.hexahq_account_id || null,
      stock_truck_delivery_day: formData.uses_stock_truck ? (formData.stock_truck_delivery_day ?? null) : null,
      ground_transit_business_days: formData.uses_stock_truck ? null : (formData.ground_transit_business_days ?? null),
      stock_truck_route: formData.uses_stock_truck ? (formData.stock_truck_route ?? null) : null,
    };

    onSubmit(transformedData);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={initialData ? 'Edit Location' : 'Create New Location'}
      submitLabel={initialData ? 'Update' : 'Create'}
      error={apiError}
      isPending={isPending}
      TransitionProps={{ onExited }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Organization</InputLabel>
              <Select
                name="organization_id"
                value={formData.organization_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, organization_id: Number(e.target.value) }))}
                label="Organization"
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="name"
              label="Location Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />
            {!initialData ? (
              /* Create mode — device_id autocomplete + power config fields */
              <>
                <Autocomplete
                  freeSolo
                  open={deviceDropdownOpen && hasFilteredDevices}
                  onOpen={() => setDeviceDropdownOpen(true)}
                  onClose={() => setDeviceDropdownOpen(false)}
                  options={allDevices}
                  getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.device_id}
                  isOptionEqualToValue={(opt, val) =>
                    typeof val === 'string' ? opt.device_id === val : opt.id === val.id
                  }
                  filterOptions={(options, { inputValue }) => filterDevices(options, inputValue)}
                  getOptionDisabled={(opt) => typeof opt !== 'string' && !!opt.location_id}
                  inputValue={formData.device_id || ''}
                  onInputChange={(_e, value, reason) => {
                    // Skip 'reset' — it fires after onChange already set the value from dropdown selection
                    if (reason !== 'reset') {
                      setFormData(prev => ({ ...prev, device_id: value.toLowerCase() }));
                    }
                    if (errors.device_id) setErrors(prev => ({ ...prev, device_id: undefined }));
                  }}
                  onChange={(_e, newValue) => {
                    const id = typeof newValue === 'string'
                      ? newValue.toLowerCase().trim()
                      : newValue?.device_id || '';
                    setFormData(prev => ({ ...prev, device_id: id }));
                    if (errors.device_id) setErrors(prev => ({ ...prev, device_id: undefined }));
                  }}
                  loading={isLoadingDevices}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {option.device_id}
                          </Typography>
                          {option.label && (
                            <Typography variant="caption" color="text.secondary">{option.label}</Typography>
                          )}
                        </Box>
                        {option.location_id ? (
                          <Chip label={option.location?.name || 'Assigned'} size="small" color="error" variant="outlined" />
                        ) : (
                          <Chip label="Unassigned" size="small" color="success" variant="outlined" />
                        )}
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Device ID"
                      error={!!errors.device_id || deviceStatus?.type === 'assigned'}
                      helperText={deviceHelperText}
                    />
                  )}
                />
                {deviceStatus?.type === 'new' && (
                  <FormControl fullWidth>
                    <InputLabel>Power Configuration</InputLabel>
                    <Select
                      name="power_configuration"
                      value={formData.power_configuration || 'Unknown'}
                      onChange={(e) => setFormData(prev => ({ ...prev, power_configuration: e.target.value as CreateLocationData['power_configuration'] }))}
                      label="Power Configuration"
                    >
                      <MenuItem value="Unknown">Unknown</MenuItem>
                      <MenuItem value="Mains-Power">Mains-Power</MenuItem>
                      <MenuItem value="Solar">Solar</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </>
            ) : (
              /* Edit mode — show devices list, assign on save */
              <LocationDevicesList
                locationId={initialData.id}
                pendingDevices={pendingDevices}
                onAddDevice={(device) => setPendingDevices(prev => [...prev, device])}
                onRemoveDevice={(device) => setPendingDevices(prev => prev.filter(d => d.id !== device.id))}
              />
            )}
            <Autocomplete
              freeSolo={false}
              disabled={!!formData.internal}
              options={account.options}
              filterOptions={(x) => x}
              getOptionLabel={account.formatLabel}
              renderOption={(props, option) => (
                <li {...props} key={option.hexahq_id}>
                  <Stack>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">ID: {option.hexahq_id}</Typography>
                  </Stack>
                </li>
              )}
              value={account.selectedOption}
              onChange={(_e, newValue) => {
                account.setSelectedOption(newValue);
                setFormData((prev) => ({ ...prev, hexahq_account_id: newValue?.hexahq_id || null }));
              }}
              inputValue={account.inputValue}
              onInputChange={(_e, newInput, reason) => {
                account.handleInputChange(_e, newInput, reason);
                if (reason === 'clear') {
                  setFormData((prev) => ({ ...prev, hexahq_account_id: null }));
                }
              }}
              isOptionEqualToValue={account.isOptionEqualToValue}
              loading={account.isLoading}
              noOptionsText={account.noOptionsText}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="HexaHQ Account"
                  helperText={
                    formData.internal
                      ? "Internal locations can't be linked to a HexaHQ account."
                      : 'Search by account name or ID. Leave blank if not linked to HexaHQ.'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {account.isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TimezoneSelect
              fullWidth
              value={formData.timezone}
              onChange={timezone => setFormData(prev => ({ ...prev, timezone }))}
            />
            <StatusMonitoringFields
              value={formData}
              onChange={patch => setFormData(prev => ({ ...prev, ...patch }))}
              alertsLabel={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip
                    title="Sends Slack alerts when hardware issues are detected: low voltage (Solar < 12.0V), stale data (no reports for 4+ hours), or unknown power configuration. Alerts are evaluated periodically and throttled to once per 24 hours. Inactive locations do not receive alerts."
                    arrow
                    placement="top"
                  >
                    <span style={{ whiteSpace: 'nowrap', borderBottom: '1px dotted', borderColor: 'inherit', cursor: 'help' }}>
                      Hardware Monitoring (Slack)
                    </span>
                  </Tooltip>
                  {formData.alerts_enabled && !formData.active && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Alerts are not sent for inactive locations.
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.internal}
                  // Immutable after create (a location is born customer or
                  // internal and never converts — server-validated); in
                  // create mode, mutually exclusive with a HexaHQ account.
                  // (Mesh membership is sync-owned since sc-6111 and can't be
                  // set from this form, so it no longer gates the switch.)
                  disabled={!!initialData || !!formData.hexahq_account_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, internal: e.target.checked }))}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip
                    title="HEXA's own facility (warehouse/tagging site). Suppresses expected-state concerns (unassigned visitor, misassigned beacon, LPA config gap — SKU drift still fires) and excludes the location from Edge Reviews and digest sending."
                    arrow
                    placement="top"
                  >
                    <span style={{ whiteSpace: 'nowrap', borderBottom: '1px dotted', borderColor: 'inherit', cursor: 'help' }}>
                      Internal Location (HEXA facility)
                    </span>
                  </Tooltip>
                  {initialData ? (
                    <Typography variant="caption" color="text.secondary">
                      Set at creation — cannot be changed.
                    </Typography>
                  ) : !!formData.hexahq_account_id ? (
                    <Typography variant="caption" color="text.secondary">
                      Clear the HexaHQ account to mark this location internal.
                    </Typography>
                  ) : null}
                </Box>
              }
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Email Digest Settings
            </Typography>

            <DigestRecipientFields
              value={formData}
              onChange={patch => {
                setFormData(prev => {
                  // Auto-populate quote_confirmation_deadline when restock day
                  // changes and the deadline is blank — keep this side effect
                  // at the wrapper layer, not in the shared field component.
                  const next = { ...prev, ...patch };
                  if (
                    'digest_restock_day_of_week' in patch
                    && patch.digest_restock_day_of_week !== null
                    && prev.show_reply_by_time
                    && (prev.quote_confirmation_deadline ?? '').trim() === ''
                  ) {
                    next.quote_confirmation_deadline = defaultDeadlineForSendDay(
                      patch.digest_restock_day_of_week as number,
                    );
                    setDeadlineWasAutoFilled(true);
                  }
                  return next;
                });
              }}
            />

            <FulfillmentFields
              value={formData}
              onChange={patch => {
                setFormData(prev => {
                  const next = { ...prev, ...patch };
                  // sc-6506: switching the reply-by toggle on pre-fills a blank
                  // deadline from the restock day (same rule as above).
                  const day = prev.digest_restock_day_of_week;
                  if (
                    patch.show_reply_by_time === true
                    && day !== null && day !== undefined
                    && (prev.quote_confirmation_deadline ?? '').trim() === ''
                  ) {
                    next.quote_confirmation_deadline = defaultDeadlineForSendDay(day);
                    setDeadlineWasAutoFilled(true);
                  }
                  return next;
                });
                if ('quote_confirmation_deadline' in patch && deadlineWasAutoFilled) {
                  setDeadlineWasAutoFilled(false);
                }
              }}
              deadlineHelper={
                deadlineWasAutoFilled
                  ? 'Auto-filled from send day. Click Update to save changes.'
                  : undefined
              }
              deadlineHelperColor={deadlineWasAutoFilled ? 'warning' : 'default'}
            />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Digest Preferences
            </Typography>
            <DigestPreferencesFields
              value={formData.digest_preferences}
              onChange={patch => setFormData(prev => ({ ...prev, ...patch }))}
            />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Account Manager (Email Footer)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Leave blank to use server defaults. Values are populated automatically after saving.
            </Typography>
            <AccountManagerFields
              value={formData.digest_preferences}
              onChange={patch => setFormData(prev => ({ ...prev, ...patch }))}
              emailError={errors.account_manager_email}
              onEmailFieldChange={() => setErrors(prev => ({ ...prev, account_manager_email: undefined }))}
            />
      </Stack>
    </FormDialog>
  );
};

/** Inline device list shown when editing a location */
const LocationDevicesList: React.FC<{
  locationId: number;
  pendingDevices: Device[];
  onAddDevice: (device: Device) => void;
  onRemoveDevice: (device: Device) => void;
}> = ({ locationId, pendingDevices, onAddDevice, onRemoveDevice }) => {
  const [isAddingDevice, setIsAddingDevice] = React.useState(false);

  const { data: devices = [] } = useQuery({
    queryKey: ['devices', { location_id: locationId }],
    queryFn: () => devicesApi.getAll({ location_id: locationId }),
  });

  const { data: unassignedDevices = [], isLoading: isLoadingUnassigned } = useQuery({
    queryKey: ['devices', { unassigned: true }],
    queryFn: () => devicesApi.getAll({ unassigned: true }),
    enabled: isAddingDevice,
  });

  // Exclude already-pending devices from the dropdown
  const pendingIds = new Set(pendingDevices.map(d => d.id));
  const availableDevices = unassignedDevices.filter(d => !pendingIds.has(d.id));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Devices</Typography>
        {!isAddingDevice && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setIsAddingDevice(true)}>
            Add Device
          </Button>
        )}
      </Box>
      {devices.length === 0 && pendingDevices.length === 0 && !isAddingDevice ? (
        <Typography variant="body2" color="text.secondary">No devices assigned</Typography>
      ) : (
        <List dense disablePadding>
          {devices.map((device: Device) => (
            <ListItem key={device.id} disableGutters>
              <ListItemText
                primary={device.device_id}
                secondary={[device.label, device.power_configuration].filter(Boolean).join(' — ')}
              />
            </ListItem>
          ))}
          {pendingDevices.map((device: Device) => (
            <ListItem
              key={`pending-${device.id}`}
              disableGutters
              secondaryAction={
                <IconButton edge="end" size="small" onClick={() => onRemoveDevice(device)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{device.device_id}</span>
                    <Chip label="Pending" size="small" color="info" variant="outlined" />
                  </Stack>
                }
                secondary={[device.label, device.power_configuration].filter(Boolean).join(' — ')}
              />
            </ListItem>
          ))}
        </List>
      )}
      {isAddingDevice && (
        <Autocomplete
          options={availableDevices}
          getOptionLabel={(opt) => opt.device_id}
          filterOptions={(options, { inputValue }) => {
            const lower = inputValue.toLowerCase();
            if (!lower) return options;
            return options.filter(d =>
              d.device_id.toLowerCase().includes(lower) ||
              (d.label || '').toLowerCase().includes(lower)
            );
          }}
          onChange={(_e, device) => {
            if (device) {
              onAddDevice(device);
              setIsAddingDevice(false);
            }
          }}
          loading={isLoadingUnassigned}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {option.device_id}
                </Typography>
                {option.label && (
                  <Typography variant="caption" color="text.secondary">{option.label}</Typography>
                )}
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select unassigned device"
              size="small"
              autoFocus
            />
          )}
          onClose={() => setIsAddingDevice(false)}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
};
